import React, { useState, useMemo } from 'react';
import SubscribeSection from '@/components/SubscribeSection';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, ExternalLink, Youtube, Zap, Star, X, ArrowUpRight, Filter } from 'lucide-react';
import {
  ALL_TOOLS,
  CATEGORIES_ORDER,
  CATEGORIES_META,
  CHANNEL_INFO,
  getToolsByCategory,
  searchTools,
  type AITool,
} from '@/lib/data';
import { springPresets } from '@/lib/motion';

// ===================== ToolCard =====================
const ToolCard: React.FC<{ tool: AITool; catMeta: typeof CATEGORIES_META[string] }> = ({ tool, catMeta }) => {
  const [hovered, setHovered] = useState(false);
  const hasVideos = tool.videos.length > 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.97 }}
      transition={springPresets.gentle}
      whileHover={{ y: -3, scale: 1.012 }}
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
      className="relative flex flex-col rounded-2xl overflow-hidden cursor-default"
      style={{
        background: '#fff',
        border: `1.5px solid ${hovered ? catMeta.color + '55' : '#e8eaed'}`,
        boxShadow: hovered
          ? `0 8px 28px -6px ${catMeta.color}22, 0 2px 8px rgba(0,0,0,0.06)`
          : '0 1px 4px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.03)',
        transition: 'border-color 0.25s, box-shadow 0.25s',
      }}
    >
      {/* Top accent bar */}
      <div
        className="absolute top-0 left-0 right-0 h-[3px]"
        style={{ background: catMeta.gradient, opacity: hovered ? 1 : 0.5, transition: 'opacity 0.25s' }}
      />

      {/* Card content */}
      <div className="flex flex-col gap-3 p-5 pt-6 flex-1">
        {/* Tool header */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2.5">
            {/* Icon */}
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center text-base font-bold shrink-0"
              style={{
                background: catMeta.bg,
                border: `1.5px solid ${catMeta.color}30`,
                color: catMeta.color,
              }}
            >
              {tool.name.charAt(0).toUpperCase()}
            </div>
            <div>
              <h3 className="font-semibold text-[15px] leading-tight text-gray-900">{tool.name}</h3>
              <span className="text-[11px] text-gray-400">{tool.domain}</span>
            </div>
          </div>
          {tool.mention_count > 1 && (
            <div
              className="flex items-center gap-1 shrink-0 px-2 py-0.5 rounded-full text-[11px] font-semibold"
              style={{ background: catMeta.badgeBg, color: catMeta.color }}
            >
              <Star size={9} />
              {tool.mention_count}次
            </div>
          )}
        </div>

        {/* Description */}
        <p className="text-sm text-gray-500 leading-relaxed flex-1">{tool.desc}</p>

        {/* Tags */}
        <div className="flex flex-wrap gap-1.5">
          {tool.tags.slice(0, 3).map(tag => (
            <span
              key={tag}
              className="px-2 py-0.5 rounded-md text-[11px] font-medium"
              style={{ background: '#f1f3f5', color: '#6b7280' }}
            >
              {tag}
            </span>
          ))}
        </div>

        {/* Video ref */}
        {hasVideos && (
          <a
            href={tool.videos[0].url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-start gap-2 p-2.5 rounded-xl group/vid transition-colors"
            style={{ background: '#fef2f2', border: '1px solid #fecaca' }}
            onClick={e => e.stopPropagation()}
          >
            <Youtube size={13} className="mt-0.5 shrink-0 text-red-400" />
            <span className="text-[11px] text-red-400 leading-relaxed line-clamp-2 group-hover/vid:text-red-500 transition-colors">
              {tool.videos[0].title}
            </span>
          </a>
        )}
      </div>

      {/* CTA */}
      <div className="px-5 pb-5">
        <a
          href={tool.url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 group/btn"
          style={{
            background: hovered ? catMeta.color : catMeta.bg,
            color: hovered ? '#fff' : catMeta.color,
            border: `1.5px solid ${catMeta.color}40`,
            boxShadow: hovered ? `0 4px 14px ${catMeta.color}40` : 'none',
          }}
          onClick={e => e.stopPropagation()}
        >
          前往工具
          <ArrowUpRight size={14} className="group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5 transition-transform" />
        </a>
      </div>
    </motion.div>
  );
};

// ===================== CategorySection =====================
const CategorySection: React.FC<{
  category: string;
  tools: AITool[];
}> = ({ category, tools }) => {
  const catMeta = CATEGORIES_META[category];
  if (!tools.length) return null;

  return (
    <motion.section
      id={`cat-${category}`}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="mb-12"
    >
      {/* Section header */}
      <div className="flex items-center gap-3 mb-5">
        <div
          className="flex items-center gap-2.5 px-4 py-2 rounded-2xl"
          style={{
            background: catMeta.bg,
            border: `1.5px solid ${catMeta.color}30`,
          }}
        >
          <span className="text-lg">{catMeta.icon}</span>
          <h2 className="font-bold text-[15px]" style={{ color: catMeta.color }}>{category}</h2>
          <span
            className="px-2 py-0.5 rounded-full text-xs font-bold font-mono"
            style={{ background: catMeta.badgeBg, color: catMeta.color }}
          >
            {tools.length}
          </span>
        </div>
        <div className="flex-1 h-px" style={{ background: `linear-gradient(to right, ${catMeta.color}30, transparent)` }} />
        <span className="text-xs text-gray-400 hidden md:block">{catMeta.desc}</span>
      </div>

      {/* Grid */}
      <motion.div
        variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.04 } } }}
        initial="hidden"
        animate="visible"
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
      >
        {tools.map(tool => (
          <ToolCard key={tool.name} tool={tool} catMeta={catMeta} />
        ))}
      </motion.div>
    </motion.section>
  );
};

// ===================== Header =====================
const Header: React.FC = () => (
  <header
    className="relative overflow-hidden border-b"
    style={{ background: 'linear-gradient(135deg, #fdf4ff 0%, #fff7ed 50%, #f0f9ff 100%)', borderColor: '#e8eaed' }}
  >
    {/* Subtle bg blobs */}
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      <div className="absolute -top-20 -left-20 w-72 h-72 rounded-full opacity-30" style={{ background: 'radial-gradient(circle, #e9d5ff, transparent 70%)' }} />
      <div className="absolute -top-10 right-1/3 w-60 h-60 rounded-full opacity-20" style={{ background: 'radial-gradient(circle, #fed7aa, transparent 70%)' }} />
    </div>

    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
        {/* Avatar */}
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={springPresets.bouncy}
          className="relative shrink-0"
        >
          <div
            className="w-20 h-20 rounded-3xl flex items-center justify-center text-3xl font-black text-white"
            style={{
              background: 'linear-gradient(135deg, #a855f7 0%, #f97316 100%)',
              boxShadow: '0 8px 24px rgba(168,85,247,0.3)',
            }}
          >
            T
          </div>
          <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-red-500 flex items-center justify-center shadow-sm">
            <Youtube size={12} className="text-white" />
          </div>
        </motion.div>

        {/* Info */}
        <motion.div
          initial={{ opacity: 0, x: -16 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ ...springPresets.gentle, delay: 0.1 }}
          className="flex-1"
        >
          <div className="flex items-center gap-2 mb-1.5">
            <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full" style={{ background: 'rgba(168,85,247,0.1)', color: '#9333ea' }}>
              YouTube 頻道
            </span>
            <span className="text-xs text-gray-400">·</span>
            <span className="text-xs text-gray-400 font-mono">{CHANNEL_INFO.totalVideos} 部影片</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-black text-gray-900 mb-1.5">
            Tracy AI 小妹
            <span
              className="ml-2 text-xl sm:text-2xl font-semibold"
              style={{ background: 'linear-gradient(to right, #a855f7, #f97316)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}
            >
              AI 工具導覽
            </span>
          </h1>
          <p className="text-sm text-gray-500 max-w-xl">{CHANNEL_INFO.description}</p>

          {/* Stats */}
          <div className="flex flex-wrap gap-3 mt-4">
            {[
              { label: 'AI 工具', value: `${CHANNEL_INFO.totalTools}+`, color: '#9333ea', bg: 'rgba(168,85,247,0.08)' },
              { label: '工具分類', value: '8', color: '#ea580c', bg: 'rgba(249,115,22,0.08)' },
              { label: '影片教學', value: `${CHANNEL_INFO.totalVideos}+`, color: '#0891b2', bg: 'rgba(6,182,212,0.08)' },
            ].map(stat => (
              <div
                key={stat.label}
                className="flex items-center gap-2 px-3 py-1.5 rounded-xl border"
                style={{ background: stat.bg, borderColor: stat.color + '25' }}
              >
                <span className="text-lg font-bold font-mono" style={{ color: stat.color }}>{stat.value}</span>
                <span className="text-xs text-gray-500">{stat.label}</span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* YouTube CTA */}
        <motion.a
          href={CHANNEL_INFO.url}
          target="_blank"
          rel="noopener noreferrer"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ ...springPresets.gentle, delay: 0.2 }}
          whileHover={{ scale: 1.04 }}
          whileTap={{ scale: 0.96 }}
          className="flex items-center gap-2 px-5 py-3 rounded-2xl font-semibold text-sm text-white shrink-0"
          style={{
            background: 'linear-gradient(135deg, #ff0000, #cc0000)',
            boxShadow: '0 4px 16px rgba(255,0,0,0.25)',
          }}
        >
          <Youtube size={16} />
          訂閱頻道
          <ExternalLink size={12} />
        </motion.a>
      </div>
    </div>
  </header>
);

// ===================== SearchBar =====================
const SearchBar: React.FC<{ value: string; onChange: (v: string) => void }> = ({ value, onChange }) => (
  <div className="relative max-w-xl">
    <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
    <input
      type="text"
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder="搜尋工具名稱、功能、標籤..."
      className="w-full pl-11 pr-10 py-2.5 rounded-2xl text-sm text-gray-800 placeholder-gray-400 outline-none transition-all"
      style={{
        background: '#fff',
        border: '1.5px solid #e2e8f0',
        boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
      }}
      onFocus={e => {
        e.currentTarget.style.borderColor = '#a855f7';
        e.currentTarget.style.boxShadow = '0 0 0 3px rgba(168,85,247,0.12)';
      }}
      onBlur={e => {
        e.currentTarget.style.borderColor = '#e2e8f0';
        e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.05)';
      }}
    />
    <AnimatePresence>
      {value && (
        <motion.button
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          onClick={() => onChange('')}
          className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full flex items-center justify-center bg-gray-200 hover:bg-gray-300 transition-colors"
        >
          <X size={11} className="text-gray-500" />
        </motion.button>
      )}
    </AnimatePresence>
  </div>
);

// ===================== CategoryPills =====================
const CategoryPills: React.FC<{
  active: string;
  counts: Record<string, number>;
  onChange: (c: string) => void;
}> = ({ active, counts, onChange }) => (
  <div className="flex flex-wrap gap-2">
    <button
      onClick={() => onChange('ALL')}
      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 border"
      style={{
        background: active === 'ALL' ? 'linear-gradient(135deg, #a855f7, #f97316)' : '#fff',
        color: active === 'ALL' ? '#fff' : '#6b7280',
        borderColor: active === 'ALL' ? 'transparent' : '#e2e8f0',
        boxShadow: active === 'ALL' ? '0 2px 8px rgba(168,85,247,0.25)' : 'none',
      }}
    >
      <Zap size={11} />
      全部
      <span className="font-mono">{ALL_TOOLS.length}</span>
    </button>
    {CATEGORIES_ORDER.map(cat => {
      const meta = CATEGORIES_META[cat];
      const isActive = active === cat;
      return (
        <button
          key={cat}
          onClick={() => onChange(cat)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 border"
          style={{
            background: isActive ? meta.bg : '#fff',
            color: isActive ? meta.color : '#6b7280',
            borderColor: isActive ? meta.color + '50' : '#e2e8f0',
            boxShadow: isActive ? `0 2px 8px ${meta.color}20` : 'none',
          }}
        >
          <span>{meta.icon}</span>
          <span className="hidden sm:inline">{cat.replace('AI ', '')}</span>
          <span className="font-mono">{counts[cat] ?? 0}</span>
        </button>
      );
    })}
  </div>
);

// ===================== Main App =====================
const App: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('ALL');
  const toolsByCategory = useMemo(() => getToolsByCategory(), []);

  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const cat of CATEGORIES_ORDER) counts[cat] = toolsByCategory[cat]?.length ?? 0;
    return counts;
  }, [toolsByCategory]);

  const searchResults = useMemo(() => searchTools(searchQuery), [searchQuery]);

  const filteredByCategory = useMemo(() => {
    if (activeCategory === 'ALL') return toolsByCategory;
    const result: Record<string, AITool[]> = {};
    for (const cat of CATEGORIES_ORDER) {
      if (cat === activeCategory) result[cat] = toolsByCategory[cat];
    }
    return result;
  }, [activeCategory, toolsByCategory]);

  const displayTools = useMemo(() => {
    if (searchQuery.trim()) {
      const byCategory: Record<string, AITool[]> = {};
      for (const tool of searchResults) {
        if (!byCategory[tool.category]) byCategory[tool.category] = [];
        byCategory[tool.category].push(tool);
      }
      return byCategory;
    }
    return filteredByCategory;
  }, [searchQuery, searchResults, filteredByCategory]);

  const displayCategories = useMemo(
    () => CATEGORIES_ORDER.filter(cat => (displayTools[cat]?.length ?? 0) > 0),
    [displayTools]
  );

  const totalShown = useMemo(
    () => Object.values(displayTools).reduce((s, arr) => s + arr.length, 0),
    [displayTools]
  );

  const isFiltered = searchQuery.trim() || activeCategory !== 'ALL';

  return (
    <div className="min-h-screen" style={{ background: '#f8fafc' }}>
      <Header />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Toolbar */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={springPresets.gentle}
          className="mb-8 space-y-3"
        >
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
            <SearchBar
              value={searchQuery}
              onChange={v => { setSearchQuery(v); setActiveCategory('ALL'); }}
            />
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <Filter size={13} />
              <span>
                顯示 <span className="font-mono font-semibold text-gray-700">{totalShown}</span> 個工具
                {isFiltered && (
                  <button
                    onClick={() => { setSearchQuery(''); setActiveCategory('ALL'); }}
                    className="ml-2 text-purple-500 hover:text-purple-700 transition-colors font-medium"
                  >
                    清除篩選
                  </button>
                )}
              </span>
            </div>
          </div>
          <CategoryPills
            active={activeCategory}
            counts={categoryCounts}
            onChange={cat => { setActiveCategory(cat); setSearchQuery(''); }}
          />
        </motion.div>

        {/* No results */}
        <AnimatePresence>
          {totalShown === 0 && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="text-center py-20"
            >
              <div className="text-5xl mb-4">🔍</div>
              <p className="text-lg font-semibold text-gray-600">找不到符合的工具</p>
              <p className="text-sm mt-1 text-gray-400">試試其他關鍵字，或瀏覽所有分類</p>
              <button
                onClick={() => { setSearchQuery(''); setActiveCategory('ALL'); }}
                className="mt-4 px-5 py-2 rounded-xl text-sm font-semibold transition-all"
                style={{ background: 'rgba(168,85,247,0.1)', color: '#9333ea', border: '1.5px solid rgba(168,85,247,0.2)' }}
              >
                查看全部工具
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Category sections */}
        <AnimatePresence mode="wait">
          <motion.div key={activeCategory + searchQuery}>
            {displayCategories.map(cat => (
              <CategorySection
                key={cat}
                category={cat}
                tools={displayTools[cat] ?? []}
              />
            ))}
          </motion.div>
        </AnimatePresence>
      </main>

      {/* Subscribe Section */}
      <SubscribeSection />

      {/* Footer */}
      <footer className="border-t mt-8" style={{ background: '#fff', borderColor: '#e2e8f0' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div
              className="w-8 h-8 rounded-xl flex items-center justify-center text-sm font-black text-white"
              style={{ background: 'linear-gradient(135deg, #a855f7, #f97316)' }}
            >
              T
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-800">Tracy AI 小妹 工具導覽</p>
              <p className="text-xs text-gray-400">整理自 YouTube 頻道影片介紹的 AI 工具</p>
            </div>
          </div>
          <div className="flex items-center gap-4 text-xs text-gray-400">
            <span className="font-mono font-semibold text-gray-600">{ALL_TOOLS.length} 個工具</span>
            <span>·</span>
            <span className="font-mono font-semibold text-gray-600">8 大分類</span>
            <span>·</span>
            <a
              href={CHANNEL_INFO.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 hover:text-red-500 transition-colors"
            >
              <Youtube size={12} className="text-red-400" />
              前往頻道
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default App;
